import cn from 'classnames';
import { Field, Form, Formik } from 'formik';
import React, { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import Button from '@/common/components/atoms/Button/Button';
import LinkLikeButton from '@/common/components/blocks/LinkLikeButton/LinkLikeButton';
import ButtonLoading from '@/common/components/molecules/ButtonLoading/ButtonLoading';
import FieldErrors from '@/common/components/molecules/FieldErrors/FieldErrors';
import Snack from '@/common/components/molecules/Snack/Snack';
import TextField from '@/common/components/molecules/TextField/TextField';
import {
  ACCESS_DENIED,
  ACCESS_DENIED_WAREHOUSE,
  FORBIDDEN,
  LOGIN_DISABLED,
  UNAUTHORIZED,
} from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import { useConfig } from '@/common/contexts/ConfigContext';
import { useRecaptcha } from '@/common/contexts/RecaptchaContext';
import getRecaptchaToken from '@/common/helpers/getRecaptchaToken';
import useTheme from '@/common/hooks/useTheme';

import {
  LoginFormDefaultProps,
  LoginFormPropTypes,
} from './LoginForm.proptypes';

const LoginForm = ({
  className,
  company,
  disabled,
  errorsIcon,
  onForgotPassword,
  onSubmit,
  onSuccess,
  registerLink,
  title,
  warehouse,
}) => {
  const { t } = useTranslation();
  const { defaultI18n } = useTheme();
  const { isLoading } = useAuth();
  const { env } = useConfig();
  const { isLoading: isLoadingRecaptcha } = useRecaptcha();

  const handleSubmit = (values, actions) => {
    let credentials = values;

    if (company && warehouse) {
      credentials = { ...credentials, company, warehouse };
    }

    getRecaptchaToken()
      .catch((error) => {
        toast.error(
          <Snack message={t('common:messages:forms.errors.recaptcha_error')} />,
        );
        throw error;
      })
      .then((recaptchaToken) =>
        onSubmit(
          { ...credentials, recaptcha_token: recaptchaToken },
          onSuccess,
        ).catch((error) => {
          const formErrors = {};

          if ([UNAUTHORIZED, FORBIDDEN].includes(error.response?.data?.code)) {
            formErrors.username = true;
            formErrors.message = error.response?.data?.message;
          }

          actions.setErrors(formErrors);

          if (error.response.status === 500) {
            toast.error(
              <Snack
                message={t('common:messages:forms.errors.server_error')}
              />,
            );
          }
        }),
      );
  };

  const setError = useCallback((errors) => {
    if (errors.message === ACCESS_DENIED) {
      return 'common:messages:login.authentication.blocked';
    }

    if (errors.message === LOGIN_DISABLED) {
      return 'common:messages:login.authentication.disabled';
    }

    if (errors.message === ACCESS_DENIED_WAREHOUSE) {
      return 'common:messages:login.authentication.warehouse_blocked';
    }

    return 'common:messages:login.authentication.error';
  }, []);

  return (
    <Formik
      initialValues={{
        username: '',
        password: '',
        recaptcha_token: '',
      }}
      disabled={isLoadingRecaptcha}
      onSubmit={handleSubmit}
    >
      {({ errors, touched }) => (
        <Form
          className={cn('form login-form', className, {
            'login-form--errors': errors.username && touched.username,
          })}
          data-qa-id="login-form"
        >
          {title ? <h2 className="h2">{title}</h2> : null}
          <Field
            as={TextField}
            className={cn('login-form__login', {
              'text-field--error': errors.username && touched.username,
            })}
            context="username-field"
            disabled={isLoading}
            id="username"
            name="username"
            required
            spellcheck={false}
          >
            {t(`${defaultI18n}:messages:forms.fields.identifier`)}
          </Field>
          <Field
            as={TextField}
            className={cn('login-form__password', {
              'text-field--error': errors.username && touched.username,
            })}
            context="password-field"
            disabled={isLoading}
            id="password"
            name="password"
            required
            spellcheck={false}
            type="password"
          >
            {t('common:messages:forms.fields.password')}
          </Field>
          <ButtonLoading
            className="login-form__submit"
            context="login-save"
            disabled={disabled}
            isLoading={isLoading}
          >
            {t('common:messages:actions.login')}
          </ButtonLoading>
          {/* Add block if onForgotPassword method is defined */}
          {onForgotPassword && (
            <div className="login-form__forgot-password">
              <Button
                onClick={onForgotPassword}
                className="link"
                context="forgot-password"
              >
                {t('common:messages:actions.forgot_password')}
              </Button>
            </div>
          )}
          {/* Add block if onNoAccount method is defined */}
          {registerLink && (
            <div className="login-form__register">
              <LinkLikeButton
                buttonSize="large"
                context="create-account"
                url={registerLink}
                variant="secondary-light"
              >
                {t('common:messages:actions.create_account')}
              </LinkLikeButton>
            </div>
          )}
          {errors.username && touched.username ? (
            <FieldErrors
              className="login-form--error-field"
              context="login-form-error"
              errors={[
                <Trans
                  i18nKey={setError(errors)}
                  values={{ env: t(`${env}:messages:title`) }}
                >
                  <strong />
                </Trans>,
              ]}
              icon={errorsIcon}
            />
          ) : null}
        </Form>
      )}
    </Formik>
  );
};

LoginForm.defaultProps = {
  ...LoginFormDefaultProps,
};

LoginForm.propTypes = {
  ...LoginFormPropTypes,
};

export default LoginForm;
